import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Col, Divider, Form, message, Row, Switch } from 'antd';
import EditLocation from '../../assets/images/edit_location.svg';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext.jsx';
import {
  AddSettingsLocation,
  AddLocationType,
  DeleteLocationType,
  GetSingleLocationType,
  GetTimezoneForCoordinates,
  UpdateLocationOperatingHours,
} from '../../apis/settings.js';
import {
  CustomInput,
  CustomTitle,
  CustomModal,
  AddItemButton,
  CustomButton,
  DropdownWithInput,
  PolygonFenceMap,
  TeamsSelector,
} from '../../components';
import { GeoFenceIcon, UndoIcon } from '@/assets/icons/icons.js';
import usePolygonEditSession from '@/hooks/usePolygonEditSession';
import ContactList from './ContactList';
import AddNewContactModal from '../Modals/AddNewContactModal.jsx';
import CustomDeleteModal from '../Modals/CustomDeleteModal.jsx';
import {
  getLocationTypeDDL,
  getTeamsDDLOFFeature,
} from '../../common/utils/dropDown';
import { useSelector } from 'react-redux';
import theme from '@/assets/theme';
import { InfoCircleFilled } from '@ant-design/icons';
import styles from './LocationForm.module.scss';
import OperatingHoursCard from './OperatingHours/OperatingHoursCard';
import EditOperatingHoursPanel from './OperatingHours/EditOperatingHoursPanel';
import {
  createOperatingHours,
  describeServerError,
  effectiveOperatingHours,
  parseOperatingHours,
} from './OperatingHours/operatingHours';

const sortPrimaryFirst = (contacts) => {
  if (!Array.isArray(contacts)) return [];
  return [...contacts].sort((a, b) => {
    if (!!a.is_primary_contact === !!b.is_primary_contact) return 0;
    return a.is_primary_contact ? -1 : 1;
  });
};

/** Server validation text for the hours, in the editor's words. */
const hoursErrorMessage = (serverMessage, hours) =>
  typeof serverMessage === 'string' && serverMessage
    ? describeServerError(serverMessage, hours)
    : 'Failed to save operating hours. Please try again.';

/** The operating-hours error from a bulk location POST response, if any. */
const createHoursError = (payload) => {
  const errors = payload?.data?.data?.errors;
  const fieldErrors = Array.isArray(errors) ? errors[0]?.errors : null;
  const first = fieldErrors?.operating_hours?.[0];
  return typeof first === 'string' ? first : null;
};

/** Default for the optional onHoursEditorOpenChange/SavingChange props. */
const ignoreHoursEditorOpen = (/** @type {boolean} */ _open) => {};

const LocationForm = ({
  locationData,
  drawerOpen,
  onClose,
  unauthorize,
  refreshLocations,
  LocationPermissions,
  hoursEditorOpen = false,
  onHoursEditorOpenChange = ignoreHoursEditorOpen,
  onHoursSavingChange = ignoreHoursEditorOpen,
}) => {
  const [form] = Form.useForm();
  const [contactForm] = Form.useForm();
  const watchedName = Form.useWatch('name', form);
  // Picking a type only updates the form, so watch it to restyle the field.
  const watchedLocationType = Form.useWatch('location_type', form);
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLatLng, setSelectedLatLng] = useState({
    lat: null,
    lng: null,
  });
  const [options, setOptions] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const isEditing = !!locationData;
  const [openModal, setOpenModal] = useState(false);
  const [contactList, setContactList] = useState(locationData?.contacts || []);
  const [isFormValid, setIsFormValid] = useState(false);
  const polygonSession = usePolygonEditSession();
  const { load: loadPolygonSession } = polygonSession;
  const [editingContact, setEditingContact] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [addressInput, setAddressInput] = useState();
  const [preferredChannel, setPreferredChannel] = useState('phone');
  const teamsList = useSelector((state) => state.teams.list);
  const [locationTypes, setLocationTypes] = useState([]);
  // "WC Office" locations are World Courier RTG codes (e.g. "CHI"): ops add
  // contacts to them, but they have no street address.
  const isWcOfficeType = locationTypes.some(
    (type) =>
      type.id === watchedLocationType &&
      (type.name || '').trim().toLowerCase() === 'wc office'
  );
  const [deleteTypeModalVisible, setDeleteTypeModalVisible] = useState(false);
  const [locationTypeToDelete, setLocationTypeToDelete] = useState(null);
  // Hours set in this drawer; until then the location's own (see below).
  const [hoursDraft, setHoursDraft] = useState(null);
  const [savingHours, setSavingHours] = useState(false);
  const hoursEditButtonRef = useRef(null);
  const wasHoursEditorOpen = useRef(hoursEditorOpen);
  // Deep links open this drawer for any location, so also respect the
  // location's own permissions (absent for new and unknown locations).
  const canEditHours =
    !LocationPermissions?.edit_contacts_only &&
    locationData?.permissions?.is_update !== false &&
    !locationData?.permissions?.edit_contacts_only;
  // Saving hours can detect the address time zone; keep it until reload.
  const [savedTimezone, setSavedTimezone] = useState(null);
  const knownTimezone = savedTimezone || locationData?.timezone_str || null;
  // Without a saved zone, look up the one the backend will save: from the
  // picked address for a new location, else from the saved coordinates
  // (an hours-only save never sends a pending address change). The backend
  // runs the same lookup, so the shown zone is the saved one.
  const lookupLat = locationData?.id
    ? locationData.latitude
    : selectedLatLng.lat;
  const lookupLng = locationData?.id
    ? locationData.longitude
    : selectedLatLng.lng;
  const [addressTimezone, setAddressTimezone] = useState(null);
  useEffect(() => {
    // The backend also skips detection without both coordinates.
    if (knownTimezone || !token || !lookupLat || !lookupLng) return undefined;
    let cancelled = false;
    GetTimezoneForCoordinates(token, lookupLat, lookupLng)
      .then((res) => {
        const zone = res?.data?.data?.data?.timezone || null;
        if (!cancelled) {
          setAddressTimezone({ lat: lookupLat, lng: lookupLng, zone });
        }
      })
      // Best effort: the zone is still detected when the location is saved.
      .catch(() => {
        if (!cancelled) {
          setAddressTimezone({ lat: lookupLat, lng: lookupLng, zone: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [knownTimezone, lookupLat, lookupLng, token]);
  const lookupSettled =
    !!addressTimezone &&
    addressTimezone.lat === lookupLat &&
    addressTimezone.lng === lookupLng;
  const previewTimezone = lookupSettled ? addressTimezone.zone : null;
  const zoneLookupPending =
    !knownTimezone && !!token && !!lookupLat && !!lookupLng && !lookupSettled;
  const detectedTimezone = knownTimezone || previewTimezone;
  // The saved schedule, else the legacy range read in the location's zone
  // (so it follows a detected zone too); null if neither.
  const savedOperatingHours = locationData?.operating_hours;
  const legacyReceivingHours = locationData?.receiving_hours;
  const locationHours = useMemo(
    () =>
      effectiveOperatingHours(
        savedOperatingHours,
        legacyReceivingHours,
        detectedTimezone
      ),
    [savedOperatingHours, legacyReceivingHours, detectedTimezone]
  );
  const operatingHours = hoursDraft ?? locationHours;
  // Legacy hours are read in the detected zone, and the editor copies its
  // hours once when it opens, so hold it until the lookup answers.
  const hoursAwaitZone =
    zoneLookupPending &&
    !hoursDraft &&
    !parseOperatingHours(savedOperatingHours) &&
    !!locationHours;

  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );

  const [teamsOptions, setTeamsOptions] = useState([]);

  const fetchLocationTypes = useCallback(async () => {
    const val = await getLocationTypeDDL(token);
    const updatedTypes = val || [];
    setLocationTypes(updatedTypes);
    return updatedTypes;
  }, [token]);
  const handleAddLocationType = async (newTypeName) => {
    const newType = { name: newTypeName };
    const res = await AddLocationType(newType);
    if (res?.data?.data?.is_success) {
      await fetchLocationTypes();
      form.setFieldsValue({
        location_type: res?.data?.data?.data?.id,
      });
    }
  };

  const handleLocationTypeSelect = (value) => {
    form.setFieldsValue({ location_type: value });
  };

  const handleLocationTypeDeleteClick = async (option) => {
    showSpin();
    try {
      const res = await GetSingleLocationType(option.id, token);
      const data = res?.data?.data;

      if (data?.is_success) {
        const locationTypeData = data?.data || {};
        setLocationTypeToDelete({
          ...option,
          assignedCount: locationTypeData?.assigned_count || 0,
        });
        setDeleteTypeModalVisible(true);
      }
    } catch (error) {
      unauthorize(error);
    } finally {
      hideSpin();
    }
  };
  const handleConfirmDeleteLocationType = async () => {
    if (!locationTypeToDelete?.id) return;

    showSpin();
    try {
      const isDeletedTypeSelected =
        form.getFieldValue('location_type') === locationTypeToDelete.id;

      const res = await DeleteLocationType(locationTypeToDelete.id, token);

      if (res?.data?.data?.is_success) {
        message.success('Location type deleted successfully.');

        const latestTypes = await fetchLocationTypes();

        if (isDeletedTypeSelected) {
          const otherType = latestTypes.find(
            (item) => item?.name?.toLowerCase() === 'other'
          );

          form.setFieldsValue({
            location_type: otherType?.id || undefined,
          });
        }

        if (refreshLocations) {
          refreshLocations();
        }

        setDeleteTypeModalVisible(false);
        setLocationTypeToDelete(null);
      }
    } catch (error) {
      unauthorize(error);
    } finally {
      hideSpin();
    }
  };

  useEffect(() => {
    getTeamsDDLOFFeature(token, 'Locations').then((val) => {
      if (val && val.length > 0) {
        setTeamsOptions(val);
      }
    });

    fetchLocationTypes();
  }, [fetchLocationTypes, token]);

  const handleSetModalOpen = (isOpen) => {
    if (!isOpen) {
      // Dismissed without Submit — discard unsaved edits since the modal opened.
      polygonSession.discard();
      setModalOpen(false);
    }
  };

  const onModalConfirm = () => {
    if (polygonSession.isValid || polygonSession.isEmpty) {
      message.success(
        polygonSession.isEmpty ? 'Polygon removed.' : 'Polygon created'
      );
      setModalOpen(false);
    } else {
      message.error('Please close the polygon before saving.');
    }
  };

  const submitNewContact = (contact) => {
    const isEditingContact = !!editingContact;
    let updatedContacts = [];

    if (isEditingContact) {
      updatedContacts = contactList.map((c) =>
        c.id === editingContact.id ? { ...c, ...contact } : c
      );
    } else {
      const newContact = { ...contact, id: Date.now() };
      updatedContacts = [...contactList, newContact];
    }

    // Checking "Make primary" transfers the flag from any existing primary
    // and moves the new primary to the top of the list.
    if (contact.is_primary_contact) {
      const primaryId = isEditingContact
        ? editingContact.id
        : updatedContacts[updatedContacts.length - 1].id;
      updatedContacts = sortPrimaryFirst(
        updatedContacts.map((c) => ({
          ...c,
          is_primary_contact: String(c.id) === String(primaryId),
        }))
      );
    }

    setContactList(updatedContacts);
    contactForm.resetFields();
    setOpenModal(false);
    setEditingContact(null);

    if (isEditing && locationData?.id) {
      const values = form.getFieldsValue();
      callSubmitLocation(values, updatedContacts, false);
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setPreferredChannel(contact.preferred_channel || 'phone');

    contactForm.setFieldsValue({
      name: contact.name,
      title: contact.title,
      email: contact.email,
      phone_number: contact.phone_number,
      is_primary_contact: contact.is_primary_contact,
      preferred_language: contact?.preferred_language,
    });
    setOpenModal(true);
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    if (!contactToDelete) return;
    const updatedContacts = contactList.filter(
      (contact) => contact.id !== contactToDelete.id
    );
    setContactList(updatedContacts);
    setDeleteModalVisible(false);
    setContactToDelete(null);

    if (locationData?.id) {
      const values = form.getFieldsValue();
      callSubmitLocation(values, updatedContacts, false);
    }
  };

  useEffect(() => {
    if (locationData) {
      form.setFieldsValue({
        name: locationData.name || '',
        address: locationData.address || '',
        location_type: locationData?.location_type?.id || '',
        is_mentionable: !!locationData.is_mentionable,
        teams: locationData.teams,
      });
      setAddressInput(locationData?.address);
      setSelectedTeams(locationData.teams || []);
      setSelectedLatLng({
        lat: locationData.latitude,
        lng: locationData.longitude,
      });
      // Legacy rows may hold a JSON-encoded string here; treat as no polygon.
      const savedPolygon = Array.isArray(locationData.polygon)
        ? locationData.polygon
        : [];
      loadPolygonSession(savedPolygon, savedPolygon.length >= 3);
      setContactList(sortPrimaryFirst(locationData.contacts || []));
      setHoursDraft(null);
      setSavedTimezone(null);
    } else {
      setAddressInput('');
      form.resetFields();
      setSelectedTeams([]);
      setSelectedLatLng({ lat: null, lng: null });
      loadPolygonSession([], false);
      setContactList([]);
      setHoursDraft(null);
      setSavedTimezone(null);
    }
  }, [locationData, form, drawerOpen, loadPolygonSession]);

  // Back from the hours editor: return focus to the card's Edit button.
  useEffect(() => {
    if (wasHoursEditorOpen.current && !hoursEditorOpen) {
      hoursEditButtonRef.current?.focus();
    }
    wasHoursEditorOpen.current = hoursEditorOpen;
  }, [hoursEditorOpen]);

  const openMapModal = () => {
    // Re-stamps the current polygon/closed values as the snapshot to revert
    // to if this modal session is cancelled, and bumps resetTrigger so
    // PolygonFenceMap re-syncs.
    polygonSession.load(polygonSession.polygon, polygonSession.isClosed);
    setModalOpen(true);
  };

  const callSubmitLocation = (values, contacts, reset) => {
    let selectedTeamIds = selectedTeams.map((team) => team.id);
    let model = {
      name: values.name,
      address: values.address || null,
      location_type: values.location_type,
      is_mentionable: !!values.is_mentionable,
      latitude: selectedLatLng.lat,
      longitude: selectedLatLng.lng,
      polygon: polygonSession.polygon,
      contacts: contacts,
      operating_hours: operatingHours,
      id: locationData?.id || null,
      teams: selectedTeamIds,
    };

    submitLocation(model, reset);
  };

  const onFinish = (values) => {
    // Safety: if more than one primary slipped through, keep the first and demote the rest.
    const primaries = contactList.filter((item) => item.is_primary_contact);
    let contacts = contactList;
    if (primaries.length > 1) {
      const keepId = primaries[0].id;
      contacts = sortPrimaryFirst(
        contactList.map((c) => ({
          ...c,
          is_primary_contact: String(c.id) === String(keepId),
        }))
      );
      setContactList(contacts);
    }

    callSubmitLocation(values, contacts, true);
  };

  const submitLocation = (model, reset) => {
    showSpin();
    setIsFormValid(true);
    let formattedContacts = model?.contacts?.map((contact, index) => {
      const rawId = contact?.id != null ? String(contact.id) : '';
      // Only send real UUIDs — Date.now() temp ids break Contact UUID PK on create.
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          rawId
        );
      return {
        key: String(index + 1),
        ...(isUuid ? { id: rawId } : {}),
        name: contact.name,
        title: contact.title,
        email: contact.email,
        phone_number: contact.phone_number || '',
        country_code: contact.country_code || '',
        preferred_language: contact.preferred_language || 'English',
        is_primary_contact: contact.is_primary_contact || false,
        preferred_channel: contact.preferred_channel || 'phone',
      };
    });

    let teamIds = selectedTeams.map((team) => team.id);
    let requestPayload = {
      name: model.name,
      address: model.address,
      location_type: model.location_type,
      is_mentionable: !!model.is_mentionable,
      latitude: model.latitude,
      longitude: model.longitude,
      polygon: model.polygon || [],
      // receiving_hours (legacy single range) is left as stored; importers
      // still write it. Existing locations save hours from the hours panel
      // only, so other saves never overwrite them.
      ...(!locationData?.id && model.operating_hours
        ? { operating_hours: model.operating_hours }
        : {}),
      contacts: formattedContacts,
      teams: teamIds,
      id: locationData?.id || null,
    };

    AddSettingsLocation(token, requestPayload)
      .then((res) => {
        const data = res?.data || null;
        if (data && data?.data?.is_success) {
          message.success(
            locationData?.id
              ? 'Location updated successfully.'
              : 'Location saved successfully.'
          );
          if (reset) {
            onClose();
            form.resetFields();
            setSelectedLatLng({ lat: null, lng: null });
            polygonSession.load([], false);
          }

          if (refreshLocations) {
            refreshLocations();
          }
        } else {
          const hoursError = createHoursError(data);
          message.error(
            hoursError
              ? `Operating hours: ${describeServerError(hoursError, model.operating_hours)}`
              : data?.data?.message
          );
        }
      })
      .catch((error) => {
        unauthorize(error);
        const hoursError = createHoursError(error?.data);
        message.error(
          hoursError
            ? `Operating hours: ${describeServerError(hoursError, model.operating_hours)}`
            : 'Failed to save location. Please try again.'
        );
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  // Existing locations save just the hours straight away; new ones keep
  // them until the location itself is saved.
  const handleSaveOperatingHours = async (hours) => {
    if (!locationData?.id) {
      setHoursDraft(hours);
      onHoursEditorOpenChange(false);
      return;
    }
    setSavingHours(true);
    onHoursSavingChange(true);
    showSpin();
    let saved = false;
    try {
      const res = await UpdateLocationOperatingHours(
        token,
        locationData.id,
        hours
      );
      const body = res?.data?.data;
      if (!body?.is_success) {
        message.error(hoursErrorMessage(body?.message, hours));
        return;
      }
      setHoursDraft(parseOperatingHours(body.data?.operating_hours) || hours);
      if (body.data?.timezone_str) setSavedTimezone(body.data.timezone_str);
      onHoursEditorOpenChange(false);
      message.success('Operating hours saved.');
      saved = true;
    } catch (error) {
      unauthorize(error);
      message.error(hoursErrorMessage(error?.data?.data?.message, hours));
    } finally {
      hideSpin();
      setSavingHours(false);
      onHoursSavingChange(false);
    }
    // After hideSpin: the shared spinner is a flag, and the list refresh
    // shows it again for its own request.
    if (saved && refreshLocations) refreshLocations();
  };

  return (
    <>
      {hoursEditorOpen && !hoursAwaitZone && (
        <EditOperatingHoursPanel
          key={locationData?.id || 'new'}
          initial={operatingHours || createOperatingHours(null)}
          detectedTimezone={detectedTimezone}
          isNewLocation={!locationData?.id}
          hasCoordinates={!!(selectedLatLng.lat && selectedLatLng.lng)}
          saving={savingHours}
          onSave={handleSaveOperatingHours}
          onCancel={() => onHoursEditorOpenChange(false)}
        />
      )}
      <Form
        form={form}
        disabled={LocationPermissions?.edit_contacts_only || false}
        layout='vertical'
        onFinish={onFinish}
        style={{
          display: hoursEditorOpen ? 'none' : 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        className={
          LocationPermissions?.edit_contacts_only ? 'disable-form' : ''
        }
      >
        <div className={styles.stickyHeader}>
          <CustomTitle
            text={isEditing ? 'Edit location' : 'Add location'}
            fontSize={20}
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>

        <div className={`custom-scrollbar ${styles.scrollBody}`}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label='Name'
                className='custom-input form-input-style'
                name='name'
                rules={[
                  { required: true, message: 'Please enter location name' },
                  {
                    pattern:
                      /^(?=.*[A-Za-zÀ-ÖØ-öø-ÿ])[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.'\-()]{2,100}$/,
                    message:
                      'Name must include at least one letter and only valid characters',
                  },
                  {
                    type: 'string',
                    min: 3,
                    message: 'Must be at least 3 characters',
                  },
                ]}
              >
                <CustomInput
                  placeholder='Enter location name'
                  bgColor='var(--light-gray)'
                  height='36px'
                  textColor='var(--text-primary)'
                  borderRadius='8px'
                  border='none'
                  placeholderColor='var(--text-secondary)'
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <div className={styles.relative}>
                <Form.Item
                  label={'Address'}
                  className='custom-input form-input-style'
                  name={'address'}
                  rules={[
                    {
                      required: !isWcOfficeType,
                      message: 'Please enter address',
                    },
                  ]}
                  style={{
                    width: '100%',
                    marginBottom: '0',
                    position: 'relative',
                  }}
                >
                  <div className={styles.inputRow}>
                    <AutoComplete
                      style={{
                        width: '88%',
                        boxShadow: 'none',
                        height: '36px',
                        backgroundColor: 'var(--light-gray)',
                        color: 'var(--text-secondary)',
                        borderRadius: '8px',
                        border: 'none',
                        marginBottom: '0px',
                      }}
                      placeholder='Enter address'
                      className={`custom-autocomplete ${styles.addressInput}`}
                      options={options}
                      value={addressInput}
                      onSelect={(value, option) => {
                        const placeId = option.placeId;
                        if (!placeId) {
                          console.error('Invalid place ID');
                          return;
                        }

                        const service =
                          new window.google.maps.places.PlacesService(
                            document.createElement('div')
                          );

                        service.getDetails({ placeId }, (place, status) => {
                          if (
                            status ===
                            window.google.maps.places.PlacesServiceStatus.OK
                          ) {
                            const formatted = place.formatted_address;

                            form.setFieldsValue({ address: formatted });
                            setAddressInput(formatted);

                            setSelectedLatLng({
                              lat: place.geometry.location.lat(),
                              lng: place.geometry.location.lng(),
                            });
                          } else {
                            console.error(
                              'Failed to fetch place details:',
                              status
                            );
                          }
                        });
                      }}
                      onSearch={(text) => {
                        if (!text) {
                          setOptions([]);
                          return;
                        }

                        const service =
                          new window.google.maps.places.AutocompleteService();

                        service.getPlacePredictions(
                          { input: text },
                          (predictions, status) => {
                            if (
                              status ===
                              window.google.maps.places.PlacesServiceStatus.OK
                            ) {
                              setOptions(
                                predictions.map((prediction) => ({
                                  label: prediction.description,
                                  value: prediction.description,
                                  placeId: prediction.place_id,
                                }))
                              );
                            } else {
                              setOptions([]);
                              console.error(
                                'Failed to fetch predictions:',
                                status
                              );
                            }
                          }
                        );
                      }}
                      onChange={(value) => {
                        setAddressInput(value);
                        form.setFieldsValue({ address: value });
                      }}
                    />
                    <span className={styles.inputSuffix}>
                      <CustomButton
                        isFormValid={
                          !(selectedLatLng.lat && selectedLatLng.lng) ||
                          LocationPermissions.edit_contacts_only
                        }
                        image={
                          polygonSession.polygon.length >= 3
                            ? undefined
                            : EditLocation
                        }
                        icon={
                          polygonSession.polygon.length >= 3 ? (
                            <GeoFenceIcon color='#fff' />
                          ) : undefined
                        }
                        iconPosition={'left'}
                        imageWidth={'12px'}
                        border='none'
                        width='28px'
                        height='28px'
                        borderRadius='50%'
                        bgColor={
                          polygonSession.polygon.length >= 3
                            ? '#000'
                            : '#f5f5f5'
                        }
                        color='var(--text-primary)'
                        boxShadow='none'
                        onClick={() => openMapModal()}
                        padding={'0'}
                        fontSize={'12px'}
                      />
                    </span>
                  </div>
                </Form.Item>
              </div>
            </Col>

            <Col span={24}>
              <Form.Item
                label='Location type'
                className='custom-input form-input-style'
                name='location_type'
                rules={[{ required: true, message: 'Select a type' }]}
              >
                <DropdownWithInput
                  options={locationTypes}
                  value={form.getFieldValue('location_type')}
                  onSelect={handleLocationTypeSelect}
                  onAdd={handleAddLocationType}
                  onDeleteOption={handleLocationTypeDeleteClick}
                  placeholder='Select...'
                  // Placeholder grey until a type is picked (the dropdown
                  // renders "Select..." like a value).
                  className={
                    locationTypes?.some(
                      (type) => type.id === watchedLocationType
                    )
                      ? undefined
                      : styles.typePlaceholder
                  }
                  addText='Add new location type'
                  inputPlaceholder='Enter new location type'
                  disabled={LocationPermissions?.edit_contacts_only}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <OperatingHoursCard
                hours={operatingHours}
                detectedTimezone={detectedTimezone}
                onEdit={() => onHoursEditorOpenChange(true)}
                disabled={!canEditHours || hoursAwaitZone}
                visible={!!drawerOpen && !hoursEditorOpen}
                editButtonRef={hoursEditButtonRef}
              />
            </Col>

            {hasValidTeams && (
              <Col span={24}>
                <Form.Item
                  label={`${theme.themeType === 2 ? 'Clients' : 'Teams'} with access`}
                  className='custom-input form-input-style'
                  name='teams'
                  rules={[{ required: true, message: 'Please select value' }]}
                >
                  <TeamsSelector
                    teamsOptions={teamsOptions}
                    selectedTeams={selectedTeams}
                    onChange={setSelectedTeams}
                    formState={isEditing ? 'edit' : 'add'}
                    disabled={LocationPermissions?.edit_contacts_only}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>
                  Enable tagging in the application
                </span>
                <Form.Item
                  name='is_mentionable'
                  valuePropName='checked'
                  noStyle
                >
                  <Switch disabled={LocationPermissions?.edit_contacts_only} />
                </Form.Item>
              </div>
            </Col>

            <Col span={24}>
              <Divider style={{ borderColor: '#D9D9D9', margin: '0px' }} />
            </Col>

            <Col span={24}>
              <CustomTitle
                text='Contacts'
                marginBottom='0px'
                fontSize={18}
                fontWeight='700'
              />
            </Col>

            {contactList?.length > 0 && (
              <Col span={24}>
                <ContactList
                  data={contactList}
                  handleEdit={handleEditContact}
                  handleDeleteClick={handleDeleteClick}
                />
              </Col>
            )}

            <Col span={24}>
              <AddItemButton
                fontSize='12px'
                marginTop='0px'
                fontWeight='600'
                text='Add contact'
                onClick={() => {
                  setEditingContact(null);
                  setPreferredChannel('phone');
                  contactForm.resetFields();
                  setOpenModal(true);
                }}
              />
            </Col>
          </Row>
        </div>

        <div className={styles.stickyFooter}>
          <Row gutter={[0, 8]} style={{ marginTop: '0' }}>
            <CustomButton
              key='button1'
              text='Save'
              bgColor='#000'
              htmlType='submit'
              color='#fff'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='48px'
              width='100%'
              borderRadius={'8px'}
              isFormValid={LocationPermissions.edit_contacts_only}
            />

            <CustomButton
              key='button2'
              text='Cancel'
              bgColor='transparent'
              color='var(--text-primary)'
              fontSize='16px'
              padding={'11.5px 0px 12px 0px'}
              fontWeight='600'
              boxShadow='none'
              height='auto'
              width='100%'
              border='none'
              marginTop='0px'
              onClick={onClose}
            />
          </Row>
        </div>

        <CustomDeleteModal
          visible={deleteModalVisible}
          onClose={() => setDeleteModalVisible(false)}
          onConfirm={handleConfirmDelete}
          title='Remove Contact'
          message='Removing this contact will prevent selecting it for future use.'
          cancelText='Cancel'
          confirmText='Remove'
          confirmColor='var(--danger)'
        />

        <CustomDeleteModal
          visible={deleteTypeModalVisible}
          onClose={() => {
            setDeleteTypeModalVisible(false);
            setLocationTypeToDelete(null);
          }}
          onConfirm={handleConfirmDeleteLocationType}
          title={`Delete “${locationTypeToDelete?.name || ''}”`}
          message={
            (locationTypeToDelete?.assignedCount || 0) === 0
              ? 'Are you sure you want to delete this location type?'
              : `${locationTypeToDelete?.assignedCount || 0} ${
                  locationTypeToDelete?.assignedCount === 1
                    ? 'location is'
                    : 'locations are'
                } assigned to this type. Deleting it will reassign ${
                  locationTypeToDelete?.assignedCount === 1 ? 'it' : 'them'
                } to “Other”.`
          }
          cancelText='Cancel'
          confirmText='Delete location type'
          confirmColor='var(--text-primary)'
          width={592}
          height={228}
        />
      </Form>

      <CustomModal
        modalOpen={modalOpen}
        setModalOpen={handleSetModalOpen}
        modalTitle='Create Geo-fence'
        modalText={
          <>
            <strong>
              {watchedName || locationData?.name || 'Untitled location'}
            </strong>
            {' • '}
            {addressInput || locationData?.address || ''}
          </>
        }
        modalSize={680}
        buttonText1={'Cancel'}
        buttonText2={'Submit'}
        isFormValid={isFormValid}
        onConfirmClick={onModalConfirm}
      >
        <div className={`polygon-map-info-banner ${styles.mapBanner}`}>
          <InfoCircleFilled className='polygon-map-info-icon' />
          <p className='polygon-map-info-copy'>
            Small geo-fences can cause false wrong-address alerts
          </p>
        </div>
        <div className='polygon-map-shell'>
          <PolygonFenceMap
            center={selectedLatLng}
            polygon={polygonSession.polygon}
            closed={polygonSession.isClosed}
            resetTrigger={polygonSession.resetTrigger}
            onPolygonChange={polygonSession.setPolygon}
            onClosedChange={polygonSession.setIsClosed}
          />
          {polygonSession.polygon.length > 0 && (
            <button
              type='button'
              className='polygon-map-undo-btn'
              onClick={polygonSession.undo}
            >
              <UndoIcon size={12} />
              <span>Undo</span>
            </button>
          )}
        </div>
      </CustomModal>

      <AddNewContactModal
        preferredChannel={preferredChannel}
        setPreferredChannel={setPreferredChannel}
        contactList={contactList}
        setEditingContact={setEditingContact}
        title={editingContact ? 'Edit contact' : 'Add contact'}
        form={contactForm}
        modalOpen={openModal}
        setModalOpen={setOpenModal}
        apiCall={submitNewContact}
        editContact={editingContact}
      />
    </>
  );
};

export default LocationForm;
